// ===========================================
// BUSTED! BLACKJACK - API CLIENT
// ===========================================

import type { ApiResponse, HealthCheckResponse } from '@busted-blackjack/shared';

const API_BASE_URL = '/api';

// Generic fetch wrapper with error handling
async function fetchApi<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  const url = `${API_BASE_URL}${endpoint}`;
  
  const defaultHeaders: HeadersInit = {
    'Content-Type': 'application/json',
  };
  
  try {
    const response = await fetch(url, {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
      credentials: 'include', // Include cookies for auth
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      return {
        success: false,
        error: data.error || {
          code: 'UNKNOWN_ERROR',
          message: 'An unknown error occurred',
        },
      };
    }
    
    return {
      success: true,
      data: data.data as T,
    };
  } catch (error) {
    return {
      success: false,
      error: {
        code: 'NETWORK_ERROR',
        message: 'Failed to connect to server',
      },
    };
  }
}

// ===========================================
// API METHODS
// ===========================================

export const api = {
  // Health check
  health: {
    check: () => fetchApi<HealthCheckResponse>('/health'),
  },
  
  // Auth endpoints
  auth: {
    // Get current user
    me: () => fetchApi<{ user: any }>('/auth/me'),
    
    // Logout
    logout: () => fetchApi<{ message: string }>('/auth/logout', { method: 'POST' }),
    
    // Refresh token
    refresh: () => fetchApi<{ message: string }>('/auth/refresh', { method: 'POST' }),

    // Accept Terms of Service
    acceptTos: () => fetchApi<{ user: any }>('/auth/accept-tos', { method: 'POST' }),

    // Get OAuth login URL (redirects, doesn't return JSON)
    getGoogleLoginUrl: (redirect?: string) => {
      const params = redirect ? `?redirect=${encodeURIComponent(redirect)}` : '';
      return `${API_BASE_URL}/auth/google${params}`;
    },
    
    getDiscordLoginUrl: (redirect?: string) => {
      const params = redirect ? `?redirect=${encodeURIComponent(redirect)}` : '';
      return `${API_BASE_URL}/auth/discord${params}`;
    },
    
    getTwitterLoginUrl: (redirect?: string) => {
      const params = redirect ? `?redirect=${encodeURIComponent(redirect)}` : '';
      return `${API_BASE_URL}/auth/twitter${params}`;
    },

    getFacebookLoginUrl: (redirect?: string) => {
      const params = redirect ? `?redirect=${encodeURIComponent(redirect)}` : '';
      return `${API_BASE_URL}/auth/facebook${params}`;
    },

    getAppleLoginUrl: (redirect?: string) => {
      const params = redirect ? `?redirect=${encodeURIComponent(redirect)}` : '';
      return `${API_BASE_URL}/auth/apple${params}`;
    },

    getWeChatLoginUrl: (redirect?: string) => {
      const params = redirect ? `?redirect=${encodeURIComponent(redirect)}` : '';
      return `${API_BASE_URL}/auth/wechat${params}`;
    },

    getMicrosoftLoginUrl: (redirect?: string) => {
      const params = redirect ? `?redirect=${encodeURIComponent(redirect)}` : '';
      return `${API_BASE_URL}/auth/microsoft${params}`;
    },

    getTikTokLoginUrl: (redirect?: string) => {
      const params = redirect ? `?redirect=${encodeURIComponent(redirect)}` : '';
      return `${API_BASE_URL}/auth/tiktok${params}`;
    },
  },
  
  // User endpoints
  users: {
    // Get current user profile with full details
    getProfile: () => fetchApi<{ user: any }>('/users/me'),
    
    // Change username
    changeUsername: (username: string) => fetchApi<{ user: any; cost: number }>(
      '/users/me/username',
      {
        method: 'PATCH',
        body: JSON.stringify({ username }),
      }
    ),
    
    // Claim daily chips
    claimDailyChips: () => fetchApi<{ amount: number; newBalance: number; nextClaimAt: string }>(
      '/users/me/daily-chips',
      { method: 'POST' }
    ),
    
    // Get user stats
    getStats: () => fetchApi<{ stats: any }>('/users/me/stats'),

    // Get public profile
    getPublicProfile: (userId: string) => fetchApi<{ user: any }>(`/users/${userId}`),

    // Search for players by username (partial match, up to 5 results)
    searchPlayer: (username: string) => fetchApi<{
      found: boolean;
      players: Array<{
        id: string;
        username: string;
        discriminator: string;
        displayName: string;
        avatarUrl: string | null;
        tableSlug: string | null;
        isOnline: boolean;
        currentTableId: string | null;
      }>;
    }>(`/users/search/${encodeURIComponent(username)}`),

    // Update user preferences
    updatePreferences: (preferences: { showRecommendations?: boolean; autoPlay?: boolean; hideSeatTutorial?: boolean }) =>
      fetchApi<{ preferences: { showRecommendations: boolean; autoPlay: boolean; hideSeatTutorial: boolean } }>(
        '/users/me/preferences',
        {
          method: 'PATCH',
          body: JSON.stringify(preferences),
        }
      ),
  },
  
  // Analytics endpoints (fire and forget, no auth required)
  analytics: {
    // Get live casino stats (public)
    getLiveStats: () => fetchApi<LiveCasinoStats>('/analytics/live-stats'),

    // Track loading screen display
    trackLoadingScreen: (data: {
      userId?: string | null;
      username?: string | null;
      targetPage: string;
      previousPage?: string | null;
      actualDuration: number;
      authLoadTime?: number | null;
      minDurationUsed: number;
      userAgent?: string | null;
      screenResolution?: string | null;
      viewportSize?: string | null;
      language?: string | null;
      visitorId: string;
      sessionId: string;
      isAuthenticated: boolean;
    }) => fetchApi<{ id: string }>('/analytics/loading-screen', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

    // Track demo hand played on landing page
    trackDemoHand: (data: {
      visitorId: string;
      sessionId?: string | null;
      bet: number;
      playerCards: string[];
      dealerCards: string[];
      playerTotal: number;
      dealerTotal: number;
      result: string;
      payout: number;
    }) => fetchApi<void>('/analytics/demo-hand', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

    // Track visitor and session (combined endpoint)
    track: (data: {
      visitorId: string;
      sessionId: string;
      userId?: string | null;
      username?: string | null;
      referrer?: string | null;
      landingPage?: string | null;
      utmSource?: string | null;
      utmMedium?: string | null;
      utmCampaign?: string | null;
      userAgent?: string | null;
      deviceType?: string | null;
      browser?: string | null;
      os?: string | null;
      screenResolution?: string | null;
      viewportSize?: string | null;
      language?: string | null;
      isAuthenticated?: boolean;
    }) => fetchApi<void>('/analytics/track', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  },

  // Feedback/support endpoints
  feedback: {
    submit: (type: 'feedback' | 'support', message: string, rating: number, contactEmail?: string, contactPhone?: string) =>
      fetchApi<{ id: string; createdAt: string }>('/feedback', {
        method: 'POST',
        body: JSON.stringify({ type, message, rating, contactEmail, contactPhone }),
      }),
  },

  // Survey endpoints (beta tester feedback)
  survey: {
    // Check if user has already completed the survey
    status: (surveyVersion?: string) =>
      fetchApi<{ completed: boolean; completedAt?: string; rewardGranted?: number }>(
        `/survey/status${surveyVersion ? `?version=${surveyVersion}` : ''}`
      ),

    submit: (
      email: string,
      responses: Record<string, unknown>,
      surveyVersion?: string,
      browserMetadata?: Record<string, unknown>
    ) =>
      fetchApi<{ id: string; createdAt: string; rewardGranted: number; newBalance: number }>('/survey', {
        method: 'POST',
        body: JSON.stringify({ email, responses, surveyVersion, browserMetadata }),
      }),
  },

  // NBA Audit endpoints (BUSTED! Buddy recommendation tracking)
  nbaAudit: {
    // Log an NBA recommendation and action
    log: (data: {
      tableSlug: string;
      handId?: string;
      recommendedAction: string;
      isDeviation?: boolean;
      actualAction?: string;
      autoExecuted?: boolean;
    }) => fetchApi<{ auditId: string }>('/nba-audit/log', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

    // Get NBA audit stats for a table
    getStats: (tableSlug: string) => fetchApi<{
      stats: {
        totalRecommendations: number;
        autoExecuted: number;
        followed: number;
        deviations: number;
      };
    }>(`/nba-audit/stats/${tableSlug}`),

    // Get BUSTED! Buddy stats for current user at a specific table
    getBuddyStats: (tableSlug: string) => fetchApi<{
      stats: {
        handsAnalyzed: number;
        adherenceRate: number;
        winRateWithBuddy: number;
        winRateWithoutBuddy: number;
        trueCountDeviations: number;
        deviationSuccessRate: number;
        insuranceAccuracy: number;
        insuranceOffered: number;
        bestStreakWithBuddy: number;
        bestStreakWithoutBuddy: number;
      };
      tableSlug: string;
    }>(`/nba-audit/buddy-stats/${tableSlug}`),

    // Record outcome for recent audit entry
    recordOutcome: (data: {
      tableSlug: string;
      outcome: string;
      isInsurance?: boolean;
    }) => fetchApi<{ recorded: boolean }>('/nba-audit/outcome', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  },

  // Cosmetics endpoints (Store, Locker, Purchase, Equip, Gift)
  cosmetics: {
    // Get all available cosmetics (store)
    getStore: (params?: { category?: string; sort?: string; search?: string }) => {
      const searchParams = new URLSearchParams();
      if (params?.category) searchParams.set('category', params.category);
      if (params?.sort) searchParams.set('sort', params.sort);
      if (params?.search) searchParams.set('search', params.search);
      const query = searchParams.toString();
      return fetchApi<{ cosmetics: CosmeticItem[] }>(`/cosmetics${query ? `?${query}` : ''}`);
    },

    // Get featured cosmetics for lobby
    getFeatured: () => fetchApi<{ featured: CosmeticItem[] }>('/cosmetics/featured'),

    // Get cosmetic categories with counts
    getCategories: () => fetchApi<{ categories: { category: string; count: number }[] }>('/cosmetics/categories'),

    // Get single cosmetic details
    getById: (id: string) => fetchApi<{ cosmetic: CosmeticItem }>(`/cosmetics/${id}`),

    // Get user's locker (owned cosmetics)
    getLocker: () => fetchApi<{
      locker: Record<string, LockerItem[]>;
      equipped: Record<string, string>;
      totalOwned: number;
    }>('/cosmetics/locker'),

    // Purchase a cosmetic
    purchase: (cosmeticId: string) => fetchApi<{
      cosmetic: { id: string; slug: string; category: string; name: string };
      cost: number;
      newBalance: number;
    }>(`/cosmetics/${cosmeticId}/purchase`, { method: 'POST' }),

    // Equip or unequip a cosmetic
    equip: (category: string, cosmeticId: string | null) => fetchApi<{
      equipped: Record<string, string>;
      message: string;
    }>('/cosmetics/equip', {
      method: 'PATCH',
      body: JSON.stringify({ category, cosmeticId }),
    }),

    // Gift a cosmetic to another user
    gift: (cosmeticId: string, recipientId?: string, recipientUsername?: string) => fetchApi<{
      cosmetic: { id: string; name: string };
      recipient: { id: string; username: string };
      cost: number;
      newBalance: number;
    }>(`/cosmetics/${cosmeticId}/gift`, {
      method: 'POST',
      body: JSON.stringify({ recipientId, recipientUsername }),
    }),
  },

  // Payments endpoints (Stripe Checkout for B-Bucks purchases)
  payments: {
    // Get available chip packages
    getPackages: () => fetchApi<{
      packages: ChipPackage[];
      dailyBargain: ChipPackage;
    }>('/payments/packages'),

    // Check if daily bargain is available
    getDailyBargainStatus: () => fetchApi<{ available: boolean }>('/payments/daily-bargain-status'),

    // Create Stripe Checkout session
    createCheckout: (packageId: string) =>
      fetchApi<{ url: string }>('/payments/create-checkout', {
        method: 'POST',
        body: JSON.stringify({ packageId }),
      }),

    // Verify payment after redirect
    verifyPayment: (sessionId: string, type?: 'chips' | 'buddy') =>
      fetchApi<{ type: string; chips?: number; newBalance?: number; expiresAt?: string }>('/payments/verify', {
        method: 'POST',
        body: JSON.stringify({ sessionId, type }),
      }),

    // Buddy subscription endpoints
    getBuddyPackages: () => fetchApi<{ packages: BuddyPackage[] }>('/payments/buddy/packages'),

    getBuddyStatus: () => fetchApi<{ active: boolean; expiresAt: string | null }>('/payments/buddy/status'),

    createBuddyCheckout: (packageId: string) =>
      fetchApi<{ url: string }>('/payments/buddy/create-checkout', {
        method: 'POST',
        body: JSON.stringify({ packageId }),
      }),
  },

  // Solo config endpoints (save/load solo table configurations)
  soloConfig: {
    // List all configs for current user
    list: () => fetchApi<{ configs: SoloConfigSummary[] }>('/solo-config'),

    // Get a specific config by ID
    get: (id: string) => fetchApi<{ config: SoloConfig }>(`/solo-config/${id}`),

    // Get a config by name (public, no auth required - for landing page)
    getByName: (name: string) => fetchApi<{ config: SoloConfig }>(`/solo-config/public/name/${encodeURIComponent(name)}`),

    // Save (create or update) a config
    save: (name: string, config: Record<string, unknown>) =>
      fetchApi<{ config: SoloConfig }>('/solo-config', {
        method: 'POST',
        body: JSON.stringify({ name, config }),
      }),

    // Delete a config
    delete: (id: string) => fetchApi<{ deleted: boolean }>(`/solo-config/${id}`, {
      method: 'DELETE',
    }),
  },

  // Demo hand history endpoints (record hands from solo/demo mode)
  demoHands: {
    // Record a completed demo hand
    record: (data: {
      sessionId: string;
      playMode: 'demo_autoplay' | 'demo_manual';
      dealerCards: Array<{ rank: string; suit: string }>;
      dealerScore: number;
      dealerBusted: boolean;
      playerHands: Array<{
        cards: Array<{ rank: string; suit: string }>;
        bet: number;
        payout: number;
        isBlackjack: boolean;
        isBusted: boolean;
        isDoubled: boolean;
        result: string;
      }>;
      totalWagered: number;
      totalPayout: number;
      durationSeconds: number;
    }) => fetchApi<{ handId: string }>('/demo-hands', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  },

  // Admin endpoints (admin only)
  admin: {
    // Get dashboard stats
    getStats: () => fetchApi<{
      stats: {
        totalUsers: number;
        usersToday: number;
        totalHands: number;
        totalFeedback: number;
        supportRequests: number;
        totalRevenue: number;
        totalSurveys: number;
      };
    }>('/admin/stats'),

    // Get all feedback/support requests
    getFeedback: () => fetchApi<{
      feedback: AdminFeedbackItem[];
    }>('/admin/feedback'),

    // Get all users
    getUsers: () => fetchApi<{
      users: AdminUserItem[];
    }>('/admin/users'),

    // Get all survey responses
    getSurveys: () => fetchApi<{
      surveys: AdminSurveyItem[];
      total: number;
    }>('/admin/surveys'),
  },

  // TODO: Table endpoints (Phase 4)
  // tables: { ... },

  // TODO: Leaderboard endpoints (Phase 4)
  // leaderboards: { ... },

  // TODO: Activity endpoints (Phase 4)
  // activity: { ... },
};

// ===========================================
// PAYMENT TYPES
// ===========================================

export interface ChipPackage {
  id: string;
  name: string;
  priceUsd: number;
  priceCents: number;
  chips: number;
  chipsPerDollar: number;
  bonusPercent: number;
  isPopular?: boolean;
  isDailyBargain?: boolean;
}

export interface BuddyPackage {
  id: string;
  name: string;
  priceUsd: number;
  priceCents: number;
  durationDays: number;
  durationLabel: string;
  savings?: string;
}

// ===========================================
// COSMETIC TYPES
// ===========================================

export interface CosmeticItem {
  id: string;
  slug: string;
  category: string;
  name: string;
  description: string | null;
  previewImageUrl: string | null;
  assetUrls: Record<string, string>;
  priceType: 'chips' | 'premium';
  priceAmount: number;
  artistName?: string | null;
  ownerCount?: number;
  owned?: boolean;
  equipped?: boolean;
  createdAt?: string;
}

export interface LockerItem {
  id: string;
  cosmeticId: string;
  slug: string;
  name: string;
  description: string | null;
  previewImageUrl: string | null;
  assetUrls: Record<string, string>;
  acquiredVia: string;
  acquiredAt: string;
  equipped: boolean;
}

// ===========================================
// LIVE CASINO STATS TYPES
// ===========================================

export interface LiveCasinoStats {
  // Today's action
  handsToday: number;
  wageredToday: number;
  blackjacksToday: number;
  biggestWinToday: { amount: number; username: string } | null;

  // All-time
  totalHands: number;
  totalPlayers: number;
  totalWagered: number;

  // Live activity
  playersOnlineNow: number;
}

// ===========================================
// SOLO CONFIG TYPES
// ===========================================

export interface SoloConfigSummary {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
}

export interface SoloConfig {
  id: string;
  name: string;
  config: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

// ===========================================
// ADMIN TYPES
// ===========================================

export interface AdminFeedbackItem {
  id: string;
  userId: string;
  username: string;
  displayName: string;
  avatarUrl: string | null;
  type: 'feedback' | 'support';
  message: string;
  rating: number;
  contactEmail: string | null;
  contactPhone: string | null;
  createdAt: string;
}

export interface AdminSurveyItem {
  id: string;
  userId: string;
  email: string;
  surveyVersion: string;
  responses: Record<string, unknown>;
  userAgent: string | null;
  createdAt: string;
  // Browser metadata
  screenWidth: number | null;
  screenHeight: number | null;
  viewportWidth: number | null;
  viewportHeight: number | null;
  devicePixelRatio: number | null;
  timezone: string | null;
  language: string | null;
  languages: string[] | null;
  isTouchDevice: boolean | null;
  colorDepth: number | null;
  hardwareConcurrency: number | null;
  deviceMemory: number | null;
  connectionType: string | null;
  referrer: string | null;
  cookiesEnabled: boolean | null;
  // Server metadata
  ipAddress: string | null;
  country: string | null;
  countryCode: string | null;
  region: string | null;
  city: string | null;
  isp: string | null;
  acceptLanguage: string | null;
  // User identity
  username: string | null;
  usernameDiscriminator: string | null;
  displayName: string | null;
  avatarUrl: string | null;
  provider: string | null;
  // Account info
  memberSince: string | null;
  balanceAtSurvey: number | null;
  // Lifetime stats
  lifetimeHandsPlayed: number | null;
  lifetimeHandsWon: number | null;
  lifetimeBlackjacks: number | null;
  lifetimeTotalWagered: number | null;
  lifetimeNetResult: number | null;
  lifetimeWinRate: number;
  // Spending
  totalDollarsSpent: number | null;
  totalChipsPurchased: number | null;
  purchaseCount: number | null;
  // Buddy
  hasBuddyActive: boolean | null;
  buddyExpiresAt: string | null;
}

export interface AdminUserItem {
  id: string;
  username: string;
  discriminator: string;
  displayName: string;
  avatarUrl: string | null;
  provider: string;
  balance: number;
  isAdmin: boolean;
  isBanned: boolean;
  countryCode: string | null;
  handsPlayed: number;
  winRate: number;
  netResult: number;
  createdAt: string;
  lastLoginAt: string | null;
}

export default api;
